import { dataLayerEventKeys, pushToDataLayer } from '@asosteam/asos-web-datalayer';
import { resetDataLayer } from '@asosteam/asos-web-gtm';

const bridgeEvents = async ({ sourcePromises, sinkPromises, logError, }) => {
    Object.values(await sourcePromises).forEach(async (source) => {
        Object.values(await sinkPromises).forEach(async (sink) => {
            source.addEventListener("message", async (message) => {
                try {
                    sink && await sink(message.data);
                }
                catch (err) {
                    logError(err);
                }
            });
        });
        source.start();
    });
};

// REF: https://experienceleague.adobe.com/docs/analytics/implementation/vars/page-vars/events/events-overview.html
var getEvents = function getEvents() {
  var eventsString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
  return eventsString.split(",").map(event => event.split("=")[0]).filter(Boolean);
};

const createAdobeSource = async (adobeDelegate) => {
    if (!adobeDelegate) {
        return null;
    }
    const { port1: privatePort, port2: publicEventTarget } = new MessageChannel();
    adobeDelegate.registerPreTrackCallback(() => {
        const data = {};
        for (const [key, value] of Object.entries(adobeDelegate)) {
            if (typeof value === "string" || typeof value === "number") {
                data[key] = value;
            }
        }
        if (!Array.isArray(data.events)) {
            data.events = getEvents(data.events);
        }
        privatePort.postMessage(data);
    });
    return publicEventTarget;
};

const createEventBusSource = async (eventBusDelegate) => {
    if (!eventBusDelegate)
        return null;
    const { port1: privatePort, port2: publicEventTarget } = new MessageChannel();
    eventBusDelegate.addWildcardListener((eventName, data) => {
        try {
            privatePort.postMessage({ events: [eventName], data });
        }
        catch (_) { } // eslint-disable-line no-empty
    });
    return publicEventTarget;
};

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : String(i);
}
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }
  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}
function _asyncToGenerator(fn) {
  return function () {
    var self = this,
      args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);
      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }
      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }
      _next(undefined);
    });
  };
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var pageDataMapper = data => {
  var {
    href,
    pathname: pagePath,
    pageTitle,
    hostname,
    queryString,
    browseCurrency,
    browseCountry,
    floor: channel,
    platform: deviceType,
    referrer: referringUrl,
    env,
    affiliateId: affid,
    isAuthenticated,
    customerId,
    firstTimeBuyer,
    geoCountry: geoIpCountryCode,
    socialTags: {
      firstName: hashedFirstName,
      lastName: hashedLastName,
      email: hashedEmail,
      billingTelephoneMobile: hashedPhoneNumber
    } = {}
  } = data;
  return {
    event: "pageData",
    href,
    pagePath,
    pageTitle,
    hostname,
    queryString,
    browseCurrency,
    browseCountry,
    channel,
    deviceType,
    referringUrl,
    env,
    marketingData: {
      affid
    },
    customerData: _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
      isAuthenticated,
      customerId,
      firstTimeBuyer,
      geoIpCountryCode
    }, hashedFirstName && {
      hashedFirstName
    }), hashedLastName && {
      hashedLastName
    }), hashedEmail && {
      hashedEmail
    }), hashedPhoneNumber && {
      hashedPhoneNumber
    })
  };
};

var CATEGORY_PAGE_TYPE = "category";
var CATEGORY_PAGE_PAGE = "Category Page";
var CATEGORY_BRAND_TYPE = "brand";
var CATEGORY_BRAND_PAGE = "Brands Page";
var SEARCH_PAGE = "Search Results";
var STATIC_CURRENCY_CODE = "GBP";

var getListType = _ref => {
  var {
    pageType,
    isBrand
  } = _ref;
  if (pageType === CATEGORY_PAGE_TYPE && isBrand) {
    return CATEGORY_BRAND_TYPE;
  }
  return pageType;
};

var getImpressionListType = _ref => {
  var {
    pageType,
    isBrand
  } = _ref;
  if (pageType === CATEGORY_PAGE_TYPE && isBrand) {
    return CATEGORY_BRAND_PAGE;
  } else if (pageType === CATEGORY_PAGE_TYPE) {
    return CATEGORY_PAGE_PAGE;
  }
  return SEARCH_PAGE;
};

var pageLoad$1 = data => {
  var {
    pageType,
    returnedProductList,
    lastPage,
    pageGridSize,
    pageNumber,
    pageSize,
    totalStyles,
    personalisationStatus,
    personalisationType,
    recommendedProductsList,
    numberOfRecommendedItems,
    totalBagItems,
    totalBagValueLocalCurrency,
    floor,
    searchTerm,
    correctedSearchTerm,
    searchType,
    searchPasses,
    failedSearchType,
    breadcrumbs: breadCrumb,
    refinements,
    currency: localCurrencyCode,
    categoryID,
    isBrand
  } = data;
  return {
    event: "view_item_list",
    listType: getListType({
      pageType,
      isBrand
    }),
    returnedProductList: returnedProductList.map(_ref => {
      var {
        id
      } = _ref;
      return id;
    }),
    lastPage,
    pageGridSize,
    pageNumber,
    pageSize,
    totalStyles,
    personalisationStatus,
    personalisationType,
    recommendedProductsList,
    numberOfRecommendedItems,
    totalBagItems,
    totalBagValueLocalCurrency,
    floor,
    searchTerm,
    correctedSearchTerm,
    searchType,
    searchPasses,
    failedSearchType,
    breadCrumb,
    refinements,
    categoryID,
    ecommerce: {
      currencyCode: STATIC_CURRENCY_CODE,
      localCurrencyCode,
      impressions: returnedProductList.map((_ref2, index) => {
        var {
          description: name,
          id: productId,
          productCode,
          priceLocalCurrency,
          brandName: brand,
          colour: variant
        } = _ref2;
        return {
          name,
          productId,
          productCode,
          priceLocalCurrency,
          brand,
          variant,
          list: getImpressionListType({
            pageType,
            isBrand
          }),
          position: index + 1
        };
      })
    }
  };
};

var saveForLater = data => {
  var {
    context,
    productIID: productId,
    priceLocalCurrency,
    brandName,
    colour,
    productCode,
    description,
    currencyCode
  } = data;
  return {
    event: "saveForLater",
    context,
    ecommerce: {
      currencyCode: STATIC_CURRENCY_CODE,
      localCurrencyCode: currencyCode,
      save: {
        products: [{
          name: description,
          priceLocalCurrency,
          brand: brandName,
          variant: colour,
          quantity: 1,
          productId,
          productCode
        }]
      }
    }
  };
};

var pageLoad = data => {
  var {
    products = [],
    bagTotalValueGBP: bagTotalValueGbp,
    bagTotalValueLocalCurrency,
    netBasketValueInGBP: netBasketValueInGbp,
    netBasketValueInNetworkCurrency,
    totalItemCount,
    spendMoreToGetPremierBannerIsShowing,
    recoverBagState,
    expiredItems,
    oneOrMoreItemsAreLowInStock,
    oneOrMoreItemsAreOutOfStock
  } = data;
  return {
    event: "view_cart",
    ecommerce: {
      viewCart: {
        products: products.map(_ref => {
          var {
            productName: name,
            productVariantId: variantId,
            productIID: productId,
            productBrand: brand,
            productQuantity: quantity,
            productPriceGBP: priceGbp,
            productPriceLocalCurrency: priceLocalCurrency,
            markedDownPrice
          } = _ref;
          return {
            name,
            variantId,
            productId,
            brand,
            quantity,
            priceGbp,
            priceLocalCurrency,
            markedDownPrice
          };
        })
      }
    },
    bagTotalValueGbp,
    bagTotalValueLocalCurrency,
    netBasketValueInGbp,
    netBasketValueInNetworkCurrency,
    totalItemCount,
    spendMoreToGetPremierBannerIsShowing,
    recoverBagState,
    expiredItems,
    oneOrMoreItemsAreLowInStock,
    oneOrMoreItemsAreOutOfStock
  };
};

var addPremierToBag = data => {
  var {
    addItemType,
    premierUpsellDisplayed,
    price,
    subscriptionType
  } = data;
  return {
    event: "add_to_cart",
    addItemType,
    subscriptionType,
    premierUpsellDisplayed,
    ecommerce: {
      add: {
        products: [{
          name: "subscription",
          id: 1,
          price,
          brand: "premier",
          category: "premier",
          quantity: 1
        }]
      }
    }
  };
};

var consentChanged = consentGroups => ({
  event: "OneTrustGroupsUpdated",
  OnetrustActiveGroups: consentGroups
});

var eventMap = {
  "tracking.pageData.ready": pageDataMapper,
  "tracking.productPage.pageLoad": data => data,
  "tracking.productPage.addToBag": data => data,
  "tracking.productPage.saveForLater": data => data,
  "tracking.productList.pageLoad.legacyGtm": pageLoad$1,
  "tracking.productList.saveForLater.legacyGtm": saveForLater,
  "tracking.asosBag.pageLoadLegacy": pageLoad,
  "tracking.asosBag.addItemLegacy": addPremierToBag,
  "tracking.savedLists": data => data,
  "onetrust.consentChanged": consentChanged
};

var handleEventImportReady$1;
var handleEventStub = new Error("\"@asosteam/asos-web-gtm\" dependency is missing");
var initialiseAsosWebGtm = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator(function* (gtmId, nonce) {
    handleEventImportReady$1 = new Promise((resolve, reject) => {
      import('@asosteam/asos-web-gtm').then(asosWebGtm => {
        var {
          handleEvent
        } = asosWebGtm.initialise(gtmId, nonce);
        resolve(handleEvent);
      }).catch(() => {
        reject(handleEventStub);
      });
    });
  });
  return function initialiseAsosWebGtm(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();
var handleEvent$2 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(function* (data) {
    if (!handleEventImportReady$1) {
      throw new Error("initialiseAsosWebGtm must be called before handleEvent");
    }
    var handleEventFunction = yield handleEventImportReady$1;
    yield handleEventFunction(data);
  });
  return function handleEvent(_x3) {
    return _ref2.apply(this, arguments);
  };
}();

var getChannelImport;
var getChannelStub = () => {
  throw new Error("\"@asosteam/asos-web-secure-script-container\" dependency is missing");
};
var getChannel = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator(function* () {
    if (!getChannelImport) {
      yield import('@asosteam/asos-web-secure-script-container').then(asosWebSecureScriptContainer => {
        getChannelImport = asosWebSecureScriptContainer.getChannel;
      }, () => {
        getChannelImport = getChannelStub;
      });
    }
    return getChannelImport();
  });
  return function getChannel() {
    return _ref.apply(this, arguments);
  };
}();

var pushEvent = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(function* (_ref) {
    var {
      id,
      eventName,
      data,
      logError,
      includeSecureScriptContainer = true,
      secureScriptContainerGtmId = undefined
    } = _ref;
    if (!(data !== null && data !== void 0 && data.event)) {
      return null;
    }
    var port;
    if (includeSecureScriptContainer) {
      ({
        port
      } = yield getChannel());
    }
    try {
      yield handleEvent$2(data);
      if (includeSecureScriptContainer) {
        port.postMessage({
          eventName,
          data,
          gtmId: secureScriptContainerGtmId
        });
      }
    } catch (err) {
      logError(err, {
        id,
        eventName,
        data
      });
    }
  });
  return function pushEvent(_x) {
    return _ref2.apply(this, arguments);
  };
}();

var isValidObject = eventData => !!eventData && !Array.isArray(eventData) && typeof eventData === "object";
var isValidString = eventData => typeof eventData === "string";
var isValidDataForGTM = (eventName, eventData) => {
  if (eventName === "onetrust.consentChanged") {
    return isValidString(eventData);
  }
  return isValidObject(eventData);
};

const handleEvent$1 = async ({ eventName, payload, id, logError, includeSecureScriptContainer, secureScriptContainerGtmId }) => {
    const eventMapper = eventMap[eventName];
    if (!isValidDataForGTM(eventName, payload.data)) {
        return;
    }
    if (eventMapper) {
        await pushEvent({
            id,
            eventName,
            data: eventMapper(payload.data),
            logError,
            includeSecureScriptContainer,
            secureScriptContainerGtmId
        });
    }
    else if (eventName.startsWith("tracking.gtm.") ||
        dataLayerEventKeys.includes(eventName)) {
        await pushEvent({
            id,
            eventName,
            data: payload.data,
            logError,
            includeSecureScriptContainer,
            secureScriptContainerGtmId
        });
    }
};
const createGtmSink = (gtmId, logError, includeSecureScriptContainer = true, nonce = undefined, secureScriptContainerGtmId = undefined) => {
    if (!gtmId || !logError) {
        return null;
    }
    initialiseAsosWebGtm(gtmId, nonce);
    const gtm = async (payload) => {
        if (!payload.events) {
            return null;
        }
        for (const eventName of payload.events) {
            await handleEvent$1({
                eventName,
                payload,
                id: gtmId,
                logError,
                includeSecureScriptContainer,
                secureScriptContainerGtmId,
            });
        }
    };
    return gtm;
};

const event1={eventKey:"View Product Page",eventTags:{eVar60:{key:"eVar60"}}};const event3={eventKey:"Successful Search",eventTags:{searchTerm:{key:"eVar25"}}};const event4={eventKey:"SearchResultNoProducts",eventTags:{}};const event5={eventKey:"RefinementsUsed",eventTags:{refinementType:{key:"prop13"}}};const event43={eventKey:"Product View",eventTags:{stockStatus:"In Stock"}};const event10={eventKey:"Add To Saved",eventTags:{productID:{key:"products"},eVar60:{key:"eVar60"}}};const event44={eventKey:"Product View",eventTags:{stockStatus:"Out Of Stock"}};const event45={eventKey:"Product View",eventTags:{stockStatus:"Partially Out Of Stock"}};const purchase={eventKey:"OrderComplete-BR",eventTags:{orderID:{key:"purchaseID"},revenue:{key:"event36"},value:{key:"quantity"}}};var manifest = {event1:event1,event3:event3,event4:event4,event5:event5,event43:event43,event10:event10,event44:event44,event45:event45,"tracking.productList.pageLoad":{eventKey:"View PLP Page",eventTags:{}},purchase:purchase};

var ADOBE_VISITS_PAGE_LOAD_COUNT_KEY = "eVar225";
var notBouncedVisitAlreadySent = false;
var captureVisitMetrics = _ref => {
  var {
    features,
    data
  } = _ref;
  var visitPageCount = parseInt(data[ADOBE_VISITS_PAGE_LOAD_COUNT_KEY]);
  if (isNaN(visitPageCount)) {
    return;
  }
  var coreAttributes = features.getCoreAttributes();
  coreAttributes.visitPageCount = visitPageCount;
  if (visitPageCount < 2) {
    delete coreAttributes.notBouncedVisit;
  } else {
    coreAttributes.notBouncedVisit = true;
  }
  features.setCoreAttributes(coreAttributes);
  if (visitPageCount === 2 && !notBouncedVisitAlreadySent) {
    notBouncedVisitAlreadySent = true;
    features.track("Not Bounced Visit");
  }
};

var mapTagValues = (value, data, tag) => {
  if (typeof value === "object") {
    if (tag === "revenue") {
      return parseInt(data[value.key] * 100);
    }
    return data[value.key];
  }
  return value;
};
var parseEventTags = (eventTags, data) => {
  var parsedEventTags = {};
  var mappedEntries = Object.entries(eventTags).map(_ref => {
    var [tag, value] = _ref;
    return [tag, mapTagValues(value, data, tag)];
  });
  for (var [key, value] of mappedEntries) {
    parsedEventTags[key] = value;
  }
  return parsedEventTags;
};

const createOptimizelySink = (featuresDelegate) => async (data) => {
    if (!featuresDelegate || !data.events) {
        return null;
    }
    let features;
    try {
        features = await featuresDelegate;
    }
    catch (_) {
        return;
    }
    const { success } = await features.onReady();
    if (!success) {
        return;
    }
    captureVisitMetrics({ features, data });
    data.events.forEach((eventName) => {
        const event = manifest[eventName];
        if (!event) {
            return;
        }
        const { eventKey, eventTags } = event;
        const parsedEventTags = parseEventTags(eventTags, data);
        features.track(eventKey, null, null, parsedEventTags);
    });
};

//ASOS specific urls, consumers can overwrite with developmentBundleUrl & productionBundleUrl args
const DEV_BUNDLE = "https://assets.adobedtm.com/27207cf31561/ceafee726e6c/launch-1710c6e6f5ba-development.min.js";
const PROD_BUNDLE = "https://assets.adobedtm.com/27207cf31561/ceafee726e6c/launch-160aa9dd03dd.min.js";
const createAdobeDTMSink = (isProduction = false, nonce = null, developmentBundleUrl = DEV_BUNDLE, productionBundleUrl = PROD_BUNDLE) => {
    const scriptSrc = isProduction ? productionBundleUrl : developmentBundleUrl;
    const isScriptLoaded = Array.from(document.getElementsByTagName("script")).find(script => script.src === scriptSrc);
    if (isScriptLoaded) {
        return;
    }
    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;
    if (nonce) {
        script.setAttribute("nonce", nonce);
    }
    const [firstScript] = document.getElementsByTagName("script");
    firstScript.parentNode.insertBefore(script, firstScript);
};

var handleEventImportReady;
var initialiseAsosWebMixpanel = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator(function* (mixpanelToken, mixpanelConfiguration) {
    handleEventImportReady = new Promise((resolve, reject) => {
      import('@asosteam/asos-web-mixpanel').then(asosWebMixpanel => {
        var {
          handleEvent
        } = asosWebMixpanel.initialise(mixpanelToken, mixpanelConfiguration);
        resolve(handleEvent);
      }).catch(() => {
        reject(new Error("\"@asosteam/asos-web-mixpanel\" dependency is missing"));
      });
    });
  });
  return function initialiseAsosWebMixpanel(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();
var handleEvent = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(function* (eventName, data) {
    if (!handleEventImportReady) {
      throw new Error("initialiseAsosWebMixpanel must be called before handleEvent");
    }
    var handleEventFunction = yield handleEventImportReady;
    yield handleEventFunction(eventName, data);
  });
  return function handleEvent(_x3, _x4) {
    return _ref2.apply(this, arguments);
  };
}();

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var lodash_merge = createCommonjsModule(function (module, exports) {
/**
 * Lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash modularize exports="npm" -o ./`
 * Copyright OpenJS Foundation and other contributors <https://openjsf.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used to detect hot functions by number of calls within a span of milliseconds. */
var HOT_COUNT = 800,
    HOT_SPAN = 16;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]',
    arrayTag = '[object Array]',
    asyncTag = '[object AsyncFunction]',
    boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    nullTag = '[object Null]',
    objectTag = '[object Object]',
    proxyTag = '[object Proxy]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    undefinedTag = '[object Undefined]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag] = typedArrayTags[arrayTag] =
typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
typedArrayTags[errorTag] = typedArrayTags[funcTag] =
typedArrayTags[mapTag] = typedArrayTags[numberTag] =
typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
typedArrayTags[setTag] = typedArrayTags[stringTag] =
typedArrayTags[weakMapTag] = false;

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/** Detect free variable `exports`. */
var freeExports = exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports && freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

/* Node.js helper references. */
var nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

/** Used for built-in method references. */
var arrayProto = Array.prototype,
    funcProto = Function.prototype,
    objectProto = Object.prototype;

/** Used to detect overreaching core-js shims. */
var coreJsData = root['__core-js_shared__'];

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString.call(Object);

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/** Built-in value references. */
var Buffer = moduleExports ? root.Buffer : undefined,
    Symbol = root.Symbol,
    Uint8Array = root.Uint8Array,
    allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined,
    getPrototype = overArg(Object.getPrototypeOf, Object),
    objectCreate = Object.create,
    propertyIsEnumerable = objectProto.propertyIsEnumerable,
    splice = arrayProto.splice,
    symToStringTag = Symbol ? Symbol.toStringTag : undefined;

var defineProperty = (function() {
  try {
    var func = getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined,
    nativeMax = Math.max,
    nativeNow = Date.now;

/* Built-in method references that are verified to be native. */
var Map = getNative(root, 'Map'),
    nativeCreate = getNative(Object, 'create');

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate = (function() {
  function object() {}
  return function(proto) {
    if (!isObject(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = nativeCreate ? nativeCreate(null) : {};
  this.size = 0;
}

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty.call(data, key) ? data[key] : undefined;
}

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return nativeCreate ? (data[key] !== undefined) : hasOwnProperty.call(data, key);
}

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED : value;
  return this;
}

// Add methods to `Hash`.
Hash.prototype.clear = hashClear;
Hash.prototype['delete'] = hashDelete;
Hash.prototype.get = hashGet;
Hash.prototype.has = hashHas;
Hash.prototype.set = hashSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return assocIndexOf(this.__data__, key) > -1;
}

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

// Add methods to `ListCache`.
ListCache.prototype.clear = listCacheClear;
ListCache.prototype['delete'] = listCacheDelete;
ListCache.prototype.get = listCacheGet;
ListCache.prototype.has = listCacheHas;
ListCache.prototype.set = listCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map || ListCache),
    'string': new Hash
  };
}

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return getMapData(this, key).get(key);
}

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return getMapData(this, key).has(key);
}

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

// Add methods to `MapCache`.
MapCache.prototype.clear = mapCacheClear;
MapCache.prototype['delete'] = mapCacheDelete;
MapCache.prototype.get = mapCacheGet;
MapCache.prototype.has = mapCacheHas;
MapCache.prototype.set = mapCacheSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new ListCache(entries);
  this.size = data.size;
}

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new ListCache;
  this.size = 0;
}

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof ListCache) {
    var pairs = data.__data__;
    if (!Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

// Add methods to `Stack`.
Stack.prototype.clear = stackClear;
Stack.prototype['delete'] = stackDelete;
Stack.prototype.get = stackGet;
Stack.prototype.has = stackHas;
Stack.prototype.set = stackSet;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray(value),
      isArg = !isArr && isArguments(value),
      isBuff = !isArr && !isArg && isBuffer(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

/**
 * This function is like `assignValue` except that it doesn't assign
 * `undefined` values.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignMergeValue(object, key, value) {
  if ((value !== undefined && !eq(object[key], value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue(object, key, value);
  }
}

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue(object, key, value);
  }
}

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && defineProperty) {
    defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */
var baseFor = createBaseFor();

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike(value) && baseGetTag(value) == argsTag;
}

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike(value) &&
    isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
}

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn(object) {
  if (!isObject(object)) {
    return nativeKeysIn(object);
  }
  var isProto = isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

/**
 * The base implementation of `_.merge` without support for multiple sources.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} [customizer] The function to customize merged values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMerge(object, source, srcIndex, customizer, stack) {
  if (object === source) {
    return;
  }
  baseFor(source, function(srcValue, key) {
    stack || (stack = new Stack);
    if (isObject(srcValue)) {
      baseMergeDeep(object, source, key, srcIndex, baseMerge, customizer, stack);
    }
    else {
      var newValue = customizer
        ? customizer(safeGet(object, key), srcValue, (key + ''), object, source, stack)
        : undefined;

      if (newValue === undefined) {
        newValue = srcValue;
      }
      assignMergeValue(object, key, newValue);
    }
  }, keysIn);
}

/**
 * A specialized version of `baseMerge` for arrays and objects which performs
 * deep merges and tracks traversed objects enabling objects with circular
 * references to be merged.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {string} key The key of the value to merge.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} mergeFunc The function to merge values.
 * @param {Function} [customizer] The function to customize assigned values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMergeDeep(object, source, key, srcIndex, mergeFunc, customizer, stack) {
  var objValue = safeGet(object, key),
      srcValue = safeGet(source, key),
      stacked = stack.get(srcValue);

  if (stacked) {
    assignMergeValue(object, key, stacked);
    return;
  }
  var newValue = customizer
    ? customizer(objValue, srcValue, (key + ''), object, source, stack)
    : undefined;

  var isCommon = newValue === undefined;

  if (isCommon) {
    var isArr = isArray(srcValue),
        isBuff = !isArr && isBuffer(srcValue),
        isTyped = !isArr && !isBuff && isTypedArray(srcValue);

    newValue = srcValue;
    if (isArr || isBuff || isTyped) {
      if (isArray(objValue)) {
        newValue = objValue;
      }
      else if (isArrayLikeObject(objValue)) {
        newValue = copyArray(objValue);
      }
      else if (isBuff) {
        isCommon = false;
        newValue = cloneBuffer(srcValue, true);
      }
      else if (isTyped) {
        isCommon = false;
        newValue = cloneTypedArray(srcValue, true);
      }
      else {
        newValue = [];
      }
    }
    else if (isPlainObject(srcValue) || isArguments(srcValue)) {
      newValue = objValue;
      if (isArguments(objValue)) {
        newValue = toPlainObject(objValue);
      }
      else if (!isObject(objValue) || isFunction(objValue)) {
        newValue = initCloneObject(srcValue);
      }
    }
    else {
      isCommon = false;
    }
  }
  if (isCommon) {
    // Recursively merge objects and arrays (susceptible to call stack limits).
    stack.set(srcValue, newValue);
    mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
    stack['delete'](srcValue);
  }
  assignMergeValue(object, key, newValue);
}

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest(func, start) {
  return setToString(overRest(func, start, identity), func + '');
}

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString = !defineProperty ? identity : function(func, string) {
  return defineProperty(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant(string),
    'writable': true
  });
};

/**
 * Creates a clone of  `buffer`.
 *
 * @private
 * @param {Buffer} buffer The buffer to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Buffer} Returns the cloned buffer.
 */
function cloneBuffer(buffer, isDeep) {
  if (isDeep) {
    return buffer.slice();
  }
  var length = buffer.length,
      result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

  buffer.copy(result);
  return result;
}

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new Uint8Array(result).set(new Uint8Array(arrayBuffer));
  return result;
}

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray(typedArray, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */
function copyArray(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      baseAssignValue(object, key, newValue);
    } else {
      assignValue(object, key, newValue);
    }
  }
  return object;
}

/**
 * Creates a function like `_.assign`.
 *
 * @private
 * @param {Function} assigner The function to assign values.
 * @returns {Function} Returns the new assigner function.
 */
function createAssigner(assigner) {
  return baseRest(function(object, sources) {
    var index = -1,
        length = sources.length,
        customizer = length > 1 ? sources[length - 1] : undefined,
        guard = length > 2 ? sources[2] : undefined;

    customizer = (assigner.length > 3 && typeof customizer == 'function')
      ? (length--, customizer)
      : undefined;

    if (guard && isIterateeCall(sources[0], sources[1], guard)) {
      customizer = length < 3 ? undefined : customizer;
      length = 1;
    }
    object = Object(object);
    while (++index < length) {
      var source = sources[index];
      if (source) {
        assigner(object, source, index, customizer);
      }
    }
    return object;
  });
}

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseFor(fromRight) {
  return function(object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[fromRight ? length : ++index];
      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }
    return object;
  };
}

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject(object) {
  return (typeof object.constructor == 'function' && !isPrototype(object))
    ? baseCreate(getPrototype(object))
    : {};
}

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike(object) && isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq(object[index], value);
  }
  return false;
}

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto;

  return value === proto;
}

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function nativeKeysIn(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString.call(value);
}

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest(func, start, transform) {
  start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return apply(func, this, otherArgs);
  };
}

/**
 * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function safeGet(object, key) {
  if (key === 'constructor' && typeof object[key] === 'function') {
    return;
  }

  if (key == '__proto__') {
    return;
  }

  return object[key];
}

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString = shortOut(baseSetToString);

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
  return isObjectLike(value) && hasOwnProperty.call(value, 'callee') &&
    !propertyIsEnumerable.call(value, 'callee');
};

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength(value.length) && !isFunction(value);
}

/**
 * This method is like `_.isArrayLike` except that it also checks if `value`
 * is an object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array-like object,
 *  else `false`.
 * @example
 *
 * _.isArrayLikeObject([1, 2, 3]);
 * // => true
 *
 * _.isArrayLikeObject(document.body.children);
 * // => true
 *
 * _.isArrayLikeObject('abc');
 * // => false
 *
 * _.isArrayLikeObject(_.noop);
 * // => false
 */
function isArrayLikeObject(value) {
  return isObjectLike(value) && isArrayLike(value);
}

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse;

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject(value) {
  if (!isObjectLike(value) || baseGetTag(value) != objectTag) {
    return false;
  }
  var proto = getPrototype(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString.call(Ctor) == objectCtorString;
}

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

/**
 * Converts `value` to a plain object flattening inherited enumerable string
 * keyed properties of `value` to own properties of the plain object.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {Object} Returns the converted plain object.
 * @example
 *
 * function Foo() {
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.assign({ 'a': 1 }, new Foo);
 * // => { 'a': 1, 'b': 2 }
 *
 * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
 * // => { 'a': 1, 'b': 2, 'c': 3 }
 */
function toPlainObject(value) {
  return copyObject(value, keysIn(value));
}

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn(object) {
  return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
}

/**
 * This method is like `_.assign` except that it recursively merges own and
 * inherited enumerable string keyed properties of source objects into the
 * destination object. Source properties that resolve to `undefined` are
 * skipped if a destination value exists. Array and plain object properties
 * are merged recursively. Other objects and value types are overridden by
 * assignment. Source objects are applied from left to right. Subsequent
 * sources overwrite property assignments of previous sources.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 0.5.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} [sources] The source objects.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = {
 *   'a': [{ 'b': 2 }, { 'd': 4 }]
 * };
 *
 * var other = {
 *   'a': [{ 'c': 3 }, { 'e': 5 }]
 * };
 *
 * _.merge(object, other);
 * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
 */
var merge = createAssigner(function(object, source, srcIndex) {
  baseMerge(object, source, srcIndex);
});

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */
function constant(value) {
  return function() {
    return value;
  };
}

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

module.exports = merge;
});

const sendDataTo = (mixpanel, processedEvents) => {
    if (typeof mixpanel !== "function") {
        return;
    }
    mixpanel(processedEvents);
};
const COOKIE_NAME = 'x-site-origin';
const DEFAULT_API_ROUTES = {
    track: "observe",
    engage: "engage",
    record: "record",
};
function getCookie(name) {
    var _a;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
        return ((_a = parts.pop()) === null || _a === void 0 ? void 0 : _a.split(';').shift()) || null;
    return null;
}
function parseRecordSessionsPercent(configValue) {
    const value = Number(String(configValue).trim());
    if (!Number.isInteger(value))
        return 0;
    return Math.min(Math.max(value, 0), 100);
}
const createMixpanelSink = (projectIdentifier, mixpanelConfiguration = {}) => {
    if (!projectIdentifier) {
        return null;
    }
    const siteOrigin = getCookie(COOKIE_NAME);
    const sessionReplayPercentage = parseRecordSessionsPercent(mixpanelConfiguration.record_sessions_percent);
    const defaultConfiguration = {
        autocapture: false,
        opt_out_persistence_by_default: true,
        disable_persistence: true,
        api_routes: DEFAULT_API_ROUTES,
        ip: false,
        debug: !!siteOrigin,
        ...(siteOrigin) && {
            api_extra_query_params: {
                [COOKIE_NAME]: siteOrigin,
            },
        },
    };
    const finalConfiguration = lodash_merge({}, defaultConfiguration, mixpanelConfiguration);
    finalConfiguration.record_sessions_percent = sessionReplayPercentage;
    initialiseAsosWebMixpanel(projectIdentifier, finalConfiguration);
    const mixpanel = async (payload) => {
        if (!payload || !Array.isArray(payload)) {
            return null;
        }
        for (const data of payload) {
            const eventName = data.event;
            if (!eventName)
                continue;
            await handleEvent(eventName, data);
        }
    };
    return mixpanel;
};

const bridgeDirectSources = ({ sinkPromises, dataLayerArguments, }) => {
    return {
        pushToDataLayer: async (definitions) => {
            const { gtm, mixpanel } = await sinkPromises;
            const processedEvents = await pushToDataLayer({
                ...dataLayerArguments,
                dataLayerSink: gtm,
            }, definitions);
            sendDataTo(mixpanel, processedEvents);
            return processedEvents;
        },
        resetDataLayer: async () => {
            await sinkPromises;
            return resetDataLayer();
        },
    };
};

const chainEntriesPromises = async (entries) => {
    const resolved = await Promise.all(Object.entries(entries).map(async ([key, value]) => [key, await value]));
    return Object.fromEntries(resolved);
};

/**
 * This callback resolves to an Adobe Analytics SDK instance.
 * @callback adobeDelegate
 * @returns {external:AdobeAnalytics.SDK|Promise<external:AdobeAnalytics.SDK>}
 */
/**
 * This callback resolves to a Features SDK instance.
 * @callback featuresDelegate
 * @returns {external:ASOSWebFeatures.FeaturesInstance|Promise<external:ASOSWebFeatures.FeaturesInstance>}
 */
/**
 * This callback resolves to an Event Bus instance.
 * @callback eventBusDelegate
 * @returns {external:ASOSWeb.EventBus|Promise<external:ASOSWeb.EventBus>}
 */
/**
 * This callback takes an error and handles logging it on the consumer side
 * @callback logError
 * @returns {void}
 */
/**
 * Initialise the Event Tracking Bridge.
 *
 * All parameters should be delegates that resolve to the required instance.
 * The Event Tracking Bridge coordinates the awaiting on each delegate
 * internally to ensure that sources, sinks, and logging do not block each
 * other from initialising as soon as possible.
 *
 * This allows the consumer to call this method as soon as possible in
 * their code in order to initialise sources and sinks before any events
 * are missed.
 *
 * @function
 * @param {Object} params
 * @param {Object} [params.sources] Object containing source delegates (adobe, eventBus, etc.)
 * @param {Object} params.sinks Object containing sink delegates (gtm, optimizely, mixpanel, etc.)
 * @param {Object} params.config Configuration object containing authentication and logging functions
 * @param {Function} params.config.logError Callback that takes an error and handles logging it
 * @param {Function} params.config.getIdTokenClaimsAsync Function to get ID token claims for authentication
 * @param {Function} params.config.isAuthenticated Function to check if user is authenticated
 * @returns {Object} Bridge instance with pushToDataLayer and resetDataLayer functions
 * @public
 * @example
 *
 * import {
 *   initialise,
 *   createAdobeSource,
 *   createEventBusSource,
 *   createGtmSink,
 *   createOptimizelySink
 * } from "asos-web-event-tracking-bridge";
 *
 * initialise({
 *   sources: {
 *     adobe: createAdobeSource(),
 *     eventBus: createEventBusSource()
 *   },
 *   sinks: {
 *     gtm: createGtmSink(),
 *     optimizely: createOptimizelySink()
 *   },
 *   config: {
 *     logError: window.newrelic.noticeError,
 *     getIdTokenClaimsAsync: auth0Client.getIdTokenClaimsAsync,
 *     isAuthenticated: auth0Client.isAuthenticated
 *   }
 * });
 */
const initialise = ({ sources = {}, sinks = {}, config, }) => {
    const sinkPromises = chainEntriesPromises(sinks);
    const sourcePromises = chainEntriesPromises(sources);
    bridgeEvents({
        sourcePromises,
        sinkPromises,
        logError: config.logError,
    }).catch(config.logError);
    return bridgeDirectSources({ sinkPromises, dataLayerArguments: config });
};

export { createAdobeDTMSink, createAdobeSource, createEventBusSource, createGtmSink, createMixpanelSink, createOptimizelySink, initialise };
//# sourceMappingURL=browser.js.map
